import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { BackendService } from 'src/app/services/backend-service';
import { CommonBackendResponseHandlerService } from 'src/app/services/common-backend-response-handler-service';
import {
  addYears,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  startOfToday,
  subHours,
} from 'date-fns';

import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  DsButtonModule,
  DsFormFieldModule,
  DsFormsModule,
  DsLoadingAreaModule,
  DsLoadingAreaConfiguration,
  DsLoadingAreaService,
  DsToastService,
  DsMessageModule,
  DsToastModel,
  DsCheckboxModule,
  DsRadioGroupModule,
  DsTableModule,
  DsModalModule,
  DsDatepickerModule,
  DsInputMaskModule,
  DsAccordionModule,
  DsBoxModule,
  DsBadgeModule,
  DsLinkModule,
  DsDatepickerConfiguration,
  SelectListOption,
  BoxSurfaceTone,
} from '@bmw-ds/components';

import { HttpErrorResponse } from '@angular/common/http';
import {
  distinctUntilChanged,
  firstValueFrom,
  startWith,
  Subscription,
} from 'rxjs';
import { CommonModule } from '@angular/common';

import { CommonBackendResponse } from 'src/app/models/common-backend-response.model';
import { enUS } from 'date-fns/locale';

import { UserProfileData } from 'src/app/models/user-profile.model';
import { SharedService } from 'src/app/services/shared-service';
import {
  AllLicenseData,
  AllSanitizedLicenseData,
  AllAuthorizedDriverFormGroup,
  AuthorizedDriverFormGroup,
  CountriesData,
  AllDeactivatedAuthorizedDriverFormGroup,
  EmployeeDriverLicenseAndInformationFileResponseData,
  EmployeeDriverLicenseAndInformationModalData,
  EmployeeDriverLicenseInlineMessage,
  EmployeeFormGroup,
  StatesData,
  AuthorizedDriverFormData,
  EmployeeFormData,
  DeactivatedAuthorizedDriverFormData,
  DeactivatedAuthorizedDriverFormGroup,
  SaveDriverLicenseInTempTableLicenseRequestData,
  EmailConsentToAuthorizedDriverRequestData,
  ReactivateAuthorizedDriverRequestData,
  SaveDriverLicenseRequestData,
  UploadConsentForAuthorizedDriverFromEmployeeDriverLicenseRequestData,
} from 'src/app/models/employee-driver-license-and-information.model';

import { AuthorizedDriverSection } from './authorized-driver-section/authorized-driver-section';
import { DeactivatedAuthorizedDriverSection } from './deactivated-authorized-driver-section/deactivated-authorized-driver-section';
import { CountryStateCodeMaintenanceData } from 'src/app/models/country-state-code-maintenance.model';
import { RelationshipMaintenanceData } from 'src/app/models/relationship-maintenance.model';

import { FormValidationService } from './form-validation-service';
import { ConsentPdfService } from './consent-pdf-service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-employee-information-and-driver-license',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DsFormFieldModule,
    DsFormsModule,
    CommonModule,
    DsButtonModule,
    DsLoadingAreaModule,
    DsMessageModule,
    DsCheckboxModule,
    DsRadioGroupModule,
    DsTableModule,
    DsModalModule,
    DsDatepickerModule,
    DsInputMaskModule,
    DsAccordionModule,
    DsBoxModule,
    DsBadgeModule,
    DsLinkModule,

    AuthorizedDriverSection,
    DeactivatedAuthorizedDriverSection,
  ],
  templateUrl: './employee-information-and-driver-license.html',
  styleUrl: './employee-information-and-driver-license.scss',
})
export class EmployeeInformationAndDriverLicense implements OnInit, OnDestroy {
  destroyRef = inject(DestroyRef);
  backendService = inject(BackendService);
  toastService = inject(DsToastService);
  loadingAreaService = inject(DsLoadingAreaService);
  sharedService = inject(SharedService);
  formBuilder = inject(FormBuilder);
  cdr = inject(ChangeDetectorRef);
  formValidationService = inject(FormValidationService);
  consentPDFService = inject(ConsentPdfService);

  currentEnvironment = environment.currentEnvironment;

  minimumFileSize: string = '0';
  maximumFileSize: string = '0';
  policyDocumentUrl: string = '';
  howToEnrollGuideDocument: string = '';

  userProfile: UserProfileData | null = null;
  allLicenseData!: AllSanitizedLicenseData;
  masterAllLicenseData!: AllLicenseData;
  allCountryStateCodeData!: CountryStateCodeMaintenanceData[];
  allRelationshipData!: RelationshipMaintenanceData[];
  allCountriesData: CountriesData[] = [];
  allStatesData: StatesData[] = [];
  allCountriesOption: SelectListOption[] = [];
  allRelationshipOption: SelectListOption[] = [];
  allStatesOption: SelectListOption[] = [];

  authorizedDriverForms!: AllAuthorizedDriverFormGroup;
  deactivatedAuthorizedDriverForms!: AllDeactivatedAuthorizedDriverFormGroup;
  employeeForm: EmployeeFormGroup = this.generateEmployeeForm();

  loadingAreaConfig: DsLoadingAreaConfiguration = {
    id: 'employeeDriverLicenseAndInformationLoader',
  };

  constructor() {}

  inlineMessage: EmployeeDriverLicenseInlineMessage = {
    message: [
      {
        type: 'forcedMVRUpdate',
        messageTexts: [],
      },
      {
        type: 'forcedDLUpdate',
        messageTexts: [],
      },
      {
        type: 'existingErrors',
        messageTexts: [],
      },
    ],
    tone: 'neutral',
    showInlineMessage: false,
  };

  modalData: EmployeeDriverLicenseAndInformationModalData = {
    showModal: false,
    confirmed: false,
    title: '',
    body: [''],
    link: '',
    type: '',
    checkboxes: [],
  };

  allLicenseFormSubscriptionMap = new Map<FormGroup, Subscription>();

  // mapFormWithSubscription(formGroup: FormGroup) {
  //   const stateProvinceCodeControl = formGroup.get(
  //     'stateProvinceCode'
  //   ) as FormControl<string>;
  //   const countryCodeControl = formGroup.get(
  //     'countryCode'
  //   ) as FormControl<string>;
  //   const last4DigitsSSNControl = formGroup.get(
  //     'last4DigitsSSN'
  //   ) as FormControl<string>;
  //   const dateOfBirthControl = formGroup.get(
  //     'dateOfBirth'
  //   ) as FormControl<string>;
  //   const base64ConsentFileControl = formGroup.get(
  //     'base64ConsentFile'
  //   ) as FormControl<string>;

  //   const stateProvinceCodeControl$ =
  //     stateProvinceCodeControl.valueChanges.pipe(
  //       startWith(stateProvinceCodeControl.value)
  //     );

  //   const base64ConsentFile$ = base64ConsentFileControl.valueChanges.pipe(
  //     startWith(base64ConsentFileControl.value)
  //   );

  //   const countryCodeControl$ = countryCodeControl.valueChanges.pipe(
  //     startWith(countryCodeControl.value)
  //   );

  //   const last4DigitsSSNControl$ = last4DigitsSSNControl.valueChanges.pipe(
  //     startWith(last4DigitsSSNControl.value)
  //   );

  //   const dateOfBirthControl$ = dateOfBirthControl.valueChanges.pipe(
  //     startWith(dateOfBirthControl.value)
  //   );

  //   const subscription = combineLatest([
  //     stateProvinceCodeControl$,
  //     countryCodeControl$,
  //     dateOfBirthControl$,
  //     base64ConsentFile$,
  //   ]).subscribe(
  //     ([stateProvinceCode, countryCode, dateOfBirth, base64ConsentFile]) => {
  //       this.checkDynamicPartsOfForm(formGroup, {
  //         stateProvinceCode,
  //         countryCode,
  //         dateOfBirth,
  //         base64ConsentFile,
  //       });
  //     }
  //   );

  //   this.allLicenseFormSubscriptionMap.set(formGroup, subscription);
  // }

  mapFormWithSubscription(formGroup: FormGroup) {
    const formGroup$ = formGroup.valueChanges
      .pipe(
        startWith(formGroup.getRawValue()),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((updatedData) => {
        this.checkDynamicPartsOfForm(formGroup, updatedData);
      });

    this.allLicenseFormSubscriptionMap.set(formGroup, formGroup$);
  }

  checkDynamicPartsOfForm(formGroup: FormGroup, updatedData: any) {
    // For employee form, disable email
    if (
      !formGroup.get('authorisedUserInd')?.value &&
      this.userProfile?.email.length !== 0
    ) {
      formGroup.get('email')?.disable();
    }

    // this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    // Check SSN requirement
    if (
      !this.isSSNRequired(
        formGroup.get('countryCode')?.value,
        formGroup.get('stateProvinceCode')?.value
      )
    ) {
      // formGroup.get('last4DigitsSSN')?.clearValidators();
      formGroup.get('last4DigitsSSN')?.disable();
    } else {
      // formGroup
      //   .get('last4DigitsSSN')
      //   ?.addValidators([
      //     Validators.required,
      //     this.formValidationService.shouldContainOnlyFourNumbers(),
      //   ]);
      formGroup.get('last4DigitsSSN')?.enable();
    }
    formGroup.get('last4DigitsSSN')?.updateValueAndValidity();

    // Check under 18
    formGroup
      .get('under18Indicator')
      ?.patchValue(this.isUnder18(formGroup.get('dateOfBirth')?.value));

    // Check if form needs to be disabled

    if (
      !formGroup.disabled &&
      formGroup.get('base64ConsentFile')?.value !== '' &&
      !formGroup.get('base64ConsentFile')?.value.includes('html')
    ) {
      formGroup.disable();
    }

    // Check if upload consent button needs to be disabled
    if (
      !(
        formGroup.get('userActionIndicator')?.value === 'R' && !formGroup.dirty
      ) &&
      !(!formGroup.get('consentInd')?.value && !formGroup.dirty)
    ) {
      // not consented and form is not dirty
      // user action indicator is r and form is not dirty
      formGroup.get('consentFile')?.disable();
    }

    // this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
  }

  commonBackendResponseHandlerService = inject(
    CommonBackendResponseHandlerService
  );

  ngOnDestroy(): void {
    this.allLicenseFormSubscriptionMap.forEach((subscription) =>
      subscription.unsubscribe()
    );
    this.allLicenseFormSubscriptionMap.clear();
  }

  async ngOnInit() {
    this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    try {
      /** Reset */
      this.inlineMessage = {
        message: [
          {
            type: 'forcedMVRUpdate',
            messageTexts: [],
          },
          {
            type: 'forcedDLUpdate',
            messageTexts: [],
          },
        ],
        tone: 'neutral',
        showInlineMessage: false,
      };

      // reset modal

      /** Form generation */
      // this.employeeForm = this.generateEmployeeForm();

      /** API calls to set forms */
      this.userProfile = await firstValueFrom(this.sharedService.userProfile$);
      const parameters = (
        await firstValueFrom(this.backendService.getParameterMaintenanceData())
      )?.reduce(
        (accumulator, params) => {
          if (params.paramName === 'CONSENT_DOC_MIN_SIZE_IN_KB') {
            accumulator.minimumFileSize = params.paramValue;
          } else if (params.paramName === 'CONSENT_DOC_SIZE_IN_KB') {
            accumulator.maximumFileSize = params.paramValue;
          } else if (params.paramName === 'COMPANY_CAR_MC_POLICY_URL') {
            accumulator.mcPolicyDocumentUrl = params.paramValue;
          } else if (params.paramName === 'COMPANY_CAR_POLICY_URL') {
            accumulator.nonMcPolicyDocumentUrl = params.paramValue;
          }
          return accumulator;
        },
        {
          minimumFileSize: '0',
          maximumFileSize: '0',
          mcPolicyDocumentUrl: '',
          nonMcPolicyDocumentUrl: '',
        }
      );
      this.minimumFileSize = parameters.minimumFileSize;
      this.maximumFileSize = parameters.maximumFileSize;
      this.policyDocumentUrl =
        this.userProfile?.hrCompany === 'BMWMC'
          ? parameters.mcPolicyDocumentUrl
          : parameters.nonMcPolicyDocumentUrl;

      this.masterAllLicenseData = await firstValueFrom(
        this.backendService.getAllDriverLicenseData(
          this.userProfile?.employeeId || ''
        )
      );

      this.allLicenseData = this.sanitizeAllLicenseData(
        structuredClone(this.masterAllLicenseData)
      );

      this.sanitizeCountryStateCodeData();

      this.allRelationshipData = await firstValueFrom(
        this.backendService.getRelationshipMaintenanceData()
      );

      this.allCountryStateCodeData = await firstValueFrom(
        this.backendService.getCountryStateCodeMaintenanceData()
      );

      this.allCountriesData = await firstValueFrom(
        this.backendService.getCountryData()
      );

      this.allStatesData = await firstValueFrom(
        this.backendService.getStateData()
      );

      this.sharedService.logger(
        'Employee DL and info, license data',
        this.allLicenseData
      );
      this.sharedService.logger(
        'Employee DL and info, relationship data',
        this.allRelationshipData.length
      );
      this.sharedService.logger(
        'Employee DL and info, country state data',
        this.allCountryStateCodeData.length
      );

      this.allCountriesOption = this.getCountriesOption();
      this.allRelationshipOption = this.getRelationshipsOption();
      this.allStatesOption = this.getStatesOption();

      /** Form generation */
      this.authorizedDriverForms = [];
      this.authorizedDriverForms = Array.from(
        { length: this.allLicenseData.authorisedUserDriverLicense.length },
        () => {
          return this.generateAuthorizedDriverForm();
        }
      );
      this.deactivatedAuthorizedDriverForms = [];
      this.deactivatedAuthorizedDriverForms = Array.from(
        {
          length:
            this.allLicenseData.inactiveAuthorisedUserDriverLicense.length,
        },
        () => {
          return this.generateDeactivatedAuthorizedDriverForm();
        }
      );

      /** Forms setup */
      this.setEmployeeForm();
      this.setAuthorizedDriverForm();
      this.setDeactivatedAuthorizedDriverForm();

      /** Form subscription setup */
      this.mapFormWithSubscription(this.employeeForm);
      this.authorizedDriverForms.forEach((authorizedDriverForm) => {
        this.mapFormWithSubscription(authorizedDriverForm);
      });

      /** Check forced error */
      this.handleShowInlineMessageForForcedUpdateMVR();
      this.handleShowInlineMessageForForceUpdateDriverLicense();
      this.handleShowInlineMessageForExistingInvalidFormData();

      this.cdr.detectChanges();
    } catch (error) {
      this.handleError(error);
    } finally {
      this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
    }
  }

  generateEmployeeForm(): EmployeeFormGroup {
    return new FormGroup({
      _isExistingData: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      consentFile: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [],
        }
      ),
      base64ConsentFile: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [],
        }
      ),
      _id: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      employeeId: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      driverLicenseNumber: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldContainOnlyAlphabetsAndNumbers(),
          ],
        }
      ),
      countryCode: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      stateProvinceCode: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      firstName: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldNotStartWithSpace(),
          ],
        }
      ),
      middleName: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [this.formValidationService.shouldNotStartWithSpace()],
        }
      ),
      lastName: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldNotStartWithSpace(),
          ],
        }
      ),
      suffix: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [this.formValidationService.shouldContainOnlyAlphabets()],
        }
      ),
      last4DigitsSSN: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldContainOnlyFourNumbers(),
          ],
        }
      ),
      zipCode: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldContainOnlyAlphabetsAndNumbers(),
          ],
        }
      ),
      driverLicenseClass: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldContainOnlyAlphabetsAndNumbers(),
          ],
        }
      ),
      addressLine1: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldNotStartWithSpace(),
          ],
        }
      ),
      addressLine2: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [this.formValidationService.shouldNotStartWithSpace()],
        }
      ),
      city: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldNotStartWithSpace(),
          ],
        }
      ),
      createdByUser: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      modUser: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      relationToEmployee: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [],
        }
      ),
      customPersonId: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      hrCompany: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      previousDriverLicenseNumber: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldContainOnlyAlphabetsAndNumbers(),
          ],
        }
      ),
      previousStateProvinceCode: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      email: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldNotStartWithSpace(),
            this.formValidationService.shouldContainOnlyEmail(),
          ],
        }
      ),
      orderMvrStatus: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),

      expiryDate: new FormControl<Date | null>(
        { value: null, disabled: false },
        {
          validators: [
            Validators.required,
            this.formValidationService.shouldContainDatesAfterToday(),
          ],
        }
      ),
      dateOfBirth: new FormControl<Date | null>(
        { value: null, disabled: false },
        {
          validators: [
            Validators.required,
            this.formValidationService.shouldContainDatesBeforeToday(),
          ],
        }
      ),
      createdDate: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      modTimeStampUtc: new FormControl<string>(
        { value: '', disabled: true },
        { nonNullable: true, validators: [Validators.required] }
      ),
      lastConsentedTimeStamp: new FormControl<string>(
        { value: '', disabled: true },
        { nonNullable: true, validators: [] }
      ),
      consentUploadedTimestamp: new FormControl<string>(
        { value: '', disabled: true },
        { nonNullable: true, validators: [Validators.required] }
      ),
      forcedDLMessage: new FormControl<string>('', { nonNullable: true }),

      userActionIndicator: new FormControl<'U' | 'N' | 'A' | 'R'>('N', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      // under18Indicator: new FormControl<boolean>(
      //   { value: false, disabled: true },
      //   {
      //     nonNullable: true,
      //     validators: [Validators.required],
      //   }
      // ),
      authorisedUserInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      mcEndorsementInd: new FormControl<boolean>(
        { value: false, disabled: false },
        {
          nonNullable: true,
          validators: [],
        }
      ),
      activeInd: new FormControl<boolean>(
        { value: true, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      primaryDriverInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      consentInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      forcedDLUpdateInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      deactivateInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      previousConsentInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
    });
  }

  generateRandomUUID() {
    return crypto.randomUUID();
  }

  generateAuthorizedDriverForm(): AuthorizedDriverFormGroup {
    return new FormGroup({
      _isExistingData: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      _id: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      consentFile: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [],
        }
      ),
      base64ConsentFile: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [],
        }
      ),
      employeeId: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      driverLicenseNumber: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldContainOnlyAlphabetsAndNumbers(),
          ],
        }
      ),
      countryCode: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      stateProvinceCode: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      firstName: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldNotStartWithSpace(),
          ],
        }
      ),
      middleName: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [this.formValidationService.shouldNotStartWithSpace()],
        }
      ),
      lastName: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldNotStartWithSpace(),
          ],
        }
      ),
      suffix: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [this.formValidationService.shouldContainOnlyAlphabets()],
        }
      ),
      last4DigitsSSN: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldContainOnlyFourNumbers(),
          ],
        }
      ),
      zipCode: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldContainOnlyAlphabetsAndNumbers(),
          ],
        }
      ),
      driverLicenseClass: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldContainOnlyAlphabetsAndNumbers(),
          ],
        }
      ),
      addressLine1: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldNotStartWithSpace(),
          ],
        }
      ),
      addressLine2: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [this.formValidationService.shouldNotStartWithSpace()],
        }
      ),
      city: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldNotStartWithSpace(),
          ],
        }
      ),
      createdByUser: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      modUser: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      relationToEmployee: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      customPersonId: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      hrCompany: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      previousDriverLicenseNumber: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldContainOnlyAlphabetsAndNumbers(),
          ],
        }
      ),
      previousStateProvinceCode: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      email: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [
            this.formValidationService.shouldNotStartWithSpace(),
            this.formValidationService.shouldContainOnlyEmail(),
          ],
        }
      ),
      orderMvrStatus: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),

      expiryDate: new FormControl<Date | null>(
        { value: null, disabled: false },
        {
          validators: [
            Validators.required,
            this.formValidationService.shouldContainDatesAfterToday(),
          ],
        }
      ),
      dateOfBirth: new FormControl<Date | null>(
        { value: null, disabled: false },
        {
          validators: [
            Validators.required,
            this.formValidationService.shouldContainDatesBeforeToday(),
          ],
        }
      ),
      createdDate: new FormControl<string>(
        { value: '', disabled: true },
        { nonNullable: true, validators: [Validators.required] }
      ),
      modTimeStampUtc: new FormControl<string>(
        { value: '', disabled: true },
        { nonNullable: true, validators: [Validators.required] }
      ),
      lastConsentedTimeStamp: new FormControl<string>(
        { value: '', disabled: true },
        { nonNullable: true, validators: [] }
      ),
      consentUploadedTimestamp: new FormControl<string>(
        { value: '', disabled: true },
        { nonNullable: true, validators: [Validators.required] }
      ),
      forcedDLMessage: new FormControl<string>('', { nonNullable: true }),

      userActionIndicator: new FormControl<'U' | 'N' | 'A' | 'R'>('N', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      under18Indicator: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      authorisedUserInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      mcEndorsementInd: new FormControl<boolean>(
        { value: false, disabled: false },
        {
          nonNullable: true,
          validators: [],
        }
      ),
      activeInd: new FormControl<boolean>(
        { value: true, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      primaryDriverInd: new FormControl<boolean>(
        { value: false, disabled: false },
        {
          nonNullable: true,
          validators: [],
        }
      ),
      consentInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      forcedDLUpdateInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      deactivateInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      previousConsentInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
    });
  }

  generateDeactivatedAuthorizedDriverForm(): DeactivatedAuthorizedDriverFormGroup {
    return new FormGroup({
      _isExistingData: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      _id: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      consentFile: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [],
        }
      ),
      base64ConsentFile: new FormControl<string>(
        { value: '', disabled: false },
        {
          nonNullable: true,
          validators: [],
        }
      ),
      employeeId: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      driverLicenseNumber: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldContainOnlyAlphabetsAndNumbers(),
          ],
        }
      ),
      countryCode: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      stateProvinceCode: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      firstName: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldNotStartWithSpace(),
          ],
        }
      ),
      middleName: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [this.formValidationService.shouldNotStartWithSpace()],
        }
      ),
      lastName: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldNotStartWithSpace(),
          ],
        }
      ),
      suffix: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [this.formValidationService.shouldContainOnlyAlphabets()],
        }
      ),
      last4DigitsSSN: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldContainOnlyFourNumbers(),
          ],
        }
      ),
      zipCode: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldContainOnlyAlphabetsAndNumbers(),
          ],
        }
      ),
      driverLicenseClass: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldContainOnlyAlphabetsAndNumbers(),
          ],
        }
      ),
      addressLine1: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldNotStartWithSpace(),
          ],
        }
      ),
      addressLine2: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [this.formValidationService.shouldNotStartWithSpace()],
        }
      ),
      city: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldNotStartWithSpace(),
          ],
        }
      ),
      createdByUser: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      modUser: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      relationToEmployee: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      customPersonId: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      hrCompany: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      previousDriverLicenseNumber: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldContainOnlyAlphabetsAndNumbers(),
          ],
        }
      ),
      previousStateProvinceCode: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      email: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.formValidationService.shouldNotStartWithSpace(),
            this.formValidationService.shouldContainOnlyEmail(),
          ],
        }
      ),
      orderMvrStatus: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),

      expiryDate: new FormControl<Date | null>(
        { value: null, disabled: true },
        {
          validators: [
            Validators.required,
            this.formValidationService.shouldContainDatesAfterToday(),
          ],
        }
      ),
      dateOfBirth: new FormControl<Date | null>(
        { value: null, disabled: true },
        {
          validators: [
            Validators.required,
            this.formValidationService.shouldContainDatesBeforeToday(),
          ],
        }
      ),
      createdDate: new FormControl<string>(
        { value: '', disabled: true },
        { nonNullable: true, validators: [Validators.required] }
      ),
      modTimeStampUtc: new FormControl<string>(
        { value: '', disabled: true },
        { nonNullable: true, validators: [Validators.required] }
      ),
      lastConsentedTimeStamp: new FormControl<string>(
        { value: '', disabled: true },
        { nonNullable: true, validators: [] }
      ),
      consentUploadedTimestamp: new FormControl<string>(
        { value: '', disabled: true },
        { nonNullable: true, validators: [Validators.required] }
      ),
      forcedDLMessage: new FormControl<string>('', { nonNullable: true }),

      userActionIndicator: new FormControl<'U' | 'N' | 'A' | 'R'>('N', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      under18Indicator: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      authorisedUserInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      mcEndorsementInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [],
        }
      ),
      activeInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      primaryDriverInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [],
        }
      ),
      consentInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      forcedDLUpdateInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      deactivateInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      previousConsentInd: new FormControl<boolean>(
        { value: false, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
    });
  }

  sanitizeCountryStateCodeData() {}

  setEmployeeForm() {
    this.employeeForm.patchValue(this.allLicenseData.employeeDriverLicense);
    if (!this.allLicenseData.employeeDriverLicense._isExistingData) {
      this.employeeForm.patchValue({
        _id: this.generateRandomUUID(),
        employeeId: this.userProfile?.employeeId,
        userActionIndicator: 'A',
        modUser: this.userProfile?.qxid,
        addressLine2: '',
        authorisedUserInd: false,
        hrCompany: this.userProfile?.companyCode,
        activeInd: true,

        addressLine1: '',
        city: '',
        countryCode: '',
        createdByUser: '',
        createdDate: '',
        customPersonId: '',
        driverLicenseClass: '',
        driverLicenseNumber: '',
        forcedDLMessage: '',
        last4DigitsSSN: '',
        lastConsentedTimeStamp: '',
        orderMvrStatus: '',
        previousDriverLicenseNumber: '',
        previousStateProvinceCode: '',
        relationToEmployee: '',
        stateProvinceCode: '',
        zipCode: '',
      });
    }
    this.employeeForm.markAsPristine();
  }

  setAuthorizedDriverForm() {
    this.authorizedDriverForms.forEach((authorizedDriverForm, index) => {
      authorizedDriverForm.patchValue(
        this.allLicenseData.authorisedUserDriverLicense[index]
      );
      authorizedDriverForm.markAsPristine();
    });
  }

  setDeactivatedAuthorizedDriverForm() {
    this.deactivatedAuthorizedDriverForms.forEach(
      (deactivatedAuthorizedDriverForm, index) => {
        deactivatedAuthorizedDriverForm.patchValue(
          this.allLicenseData.inactiveAuthorisedUserDriverLicense[index]
        );
        deactivatedAuthorizedDriverForm.markAsPristine();
      }
    );
  }

  checkInvalidDataInForm(
    formGroup: FormGroup,
    skipExpiryValidation = false,
    checkDuplicateDriverLicenseNumbers = true
  ): string {
    // check for date based error
    let formErrors = this.getFormErrors(formGroup);
    // this.sharedService.logger(
    //   'Employee driver license - Error in form',
    //   formErrors
    // );

    const sanitizedDateOfBirth =
      formGroup?.get('dateOfBirth')?.value instanceof Date
        ? formGroup?.get('dateOfBirth')?.value
        : new Date(formGroup?.get('dateOfBirth')?.value);

    const sanitizedExpiryDate =
      formGroup?.get('expiryDate')?.value instanceof Date
        ? formGroup?.get('expiryDate')?.value
        : new Date(formGroup?.get('expiryDate')?.value);

    const isRelationshipInvalidForAuthorizedDriver =
      this.allRelationshipData.filter((data) => {
        return (
          data.activeIndicator === 'N' &&
          data.relationshipCode === formGroup?.get('relationToEmployee')?.value
        );
      })[0];

    if (formErrors) {
      if (
        formGroup.get('firstName')?.value ||
        formGroup.get('middleName')?.value ||
        formGroup.get('lastName')?.value
      ) {
        formErrors = `For, ${formGroup.get('firstName')?.value} ${formGroup.get('middleName')?.value} ${formGroup.get('lastName')?.value} ${formErrors}.`;
      } else {
        formErrors = `For, ${formErrors}.`;
      }
    }

    if (!isBefore(sanitizedDateOfBirth, startOfToday())) {
      if (formGroup.get('dateOfBirth')?.value) {
        formErrors = `${formErrors} Date of birth must be past date.`;
      }
    }

    if (!skipExpiryValidation) {
      if (
        !isAfter(sanitizedExpiryDate, new Date()) &&
        !isSameDay(sanitizedExpiryDate, new Date())
      ) {
        if (formGroup.get('expiryDate')?.value) {
          formErrors = `${formErrors} Expiry date must be future date.`;
        }
      }
    }

    if (
      isRelationshipInvalidForAuthorizedDriver &&
      formGroup.get('authorisedUserInd')?.value === true
    ) {
      if (formErrors) {
        formErrors = `${formErrors} relationship is invalid.`;
      } else {
        formErrors = `For, ${formGroup.get('firstName')?.value} ${formGroup.get('middleName')?.value} ${formGroup.get('lastName')?.value} relationship is invalid.`;
      }
    }

    const allDriverLicenseNumbers = [];
    allDriverLicenseNumbers.push(
      this.employeeForm.get('driverLicenseNumber')?.value
    );

    this.authorizedDriverForms.forEach((authorizedDriverFrom) => {
      allDriverLicenseNumbers.push(
        authorizedDriverFrom.get('driverLicenseNumber')?.value
      );
    });

    this.deactivatedAuthorizedDriverForms.forEach((deactivatedAuthorizedDriverForm) => {
      allDriverLicenseNumbers.push(
        deactivatedAuthorizedDriverForm.get('driverLicenseNumber')?.value
      );
    });

    if (
      checkDuplicateDriverLicenseNumbers &&
      new Set(allDriverLicenseNumbers).size !== allDriverLicenseNumbers.length
    ) {
      if (formErrors) {
        formErrors = `${formErrors} some entered driver license numbers are duplicated.`;
      } else {
        formErrors = `Some entered driver license numbers are duplicated.`;
      }
    }

    this.sharedService.logger('Emp DL and info al error ', formErrors);
    // formGroup.markAllAsDirty();
    formGroup.markAllAsTouched();
    return formErrors;
  }

  sanitizeAllLicenseData(
    allLicenseData: AllLicenseData
  ): AllSanitizedLicenseData {
    // Change Y, N to boolean
    // under18Indicator, primaryDriverInd, previousConsentInd, mcEndorsementInd, forcedDLUpdateInd, deactivateInd, consentInd, authorisedUserInd
    // Change date string to Density compatible date
    // lastConsentedTimeStamp, modTimeStampUtc, expiryDate, createdDate, dateOfBirth, consentUploadedTimestamp
    const sanitizedAllLicenseData: AllSanitizedLicenseData = {
      authorisedUserDriverLicense: [],
      employeeDriverLicense: {
        _isExistingData: allLicenseData.employeeDriverLicense.customPersonId
          ? true
          : false,
        _id: this.generateRandomUUID(),
        employeeId: allLicenseData.employeeDriverLicense.employeeId?.trim(),
        driverLicenseNumber:
          allLicenseData.employeeDriverLicense.driverLicenseNumber?.trim(),
        countryCode: allLicenseData.employeeDriverLicense.countryCode?.trim(),
        stateProvinceCode:
          allLicenseData.employeeDriverLicense.stateProvinceCode?.trim(),
        firstName:
          allLicenseData.employeeDriverLicense.firstName?.trim() ||
          this.userProfile?.firstName ||
          '',
        middleName:
          allLicenseData.employeeDriverLicense.middleName?.trim() ||
          this.userProfile?.middleName ||
          '',
        lastName:
          allLicenseData.employeeDriverLicense.lastName?.trim() ||
          this.userProfile?.lastName ||
          '',
        suffix: allLicenseData.employeeDriverLicense.suffix?.trim() || '',
        last4DigitsSSN:
          allLicenseData.employeeDriverLicense.last4DigitsSSN?.trim(),
        zipCode: allLicenseData.employeeDriverLicense.zipCode?.trim(),
        driverLicenseClass:
          allLicenseData.employeeDriverLicense.driverLicenseClass?.trim(),
        addressLine1: allLicenseData.employeeDriverLicense.addressLine1?.trim(),
        addressLine2: allLicenseData.employeeDriverLicense.addressLine2?.trim(),
        city: allLicenseData.employeeDriverLicense.city?.trim(),
        createdByUser:
          allLicenseData.employeeDriverLicense.createdByUser?.trim(),
        modUser: allLicenseData.employeeDriverLicense.modUser?.trim(),
        email:
          allLicenseData.employeeDriverLicense.email?.trim() ||
          this.userProfile?.email ||
          '',
        orderMvrStatus:
          allLicenseData.employeeDriverLicense.orderMvrStatus?.trim(),
        previousStateProvinceCode:
          allLicenseData.employeeDriverLicense.previousStateProvinceCode?.trim(),
        relationToEmployee:
          allLicenseData.employeeDriverLicense.relationToEmployee?.trim() || '',
        customPersonId:
          allLicenseData.employeeDriverLicense.customPersonId?.trim(),
        hrCompany: allLicenseData.employeeDriverLicense.hrCompany?.trim(),
        previousDriverLicenseNumber:
          allLicenseData.employeeDriverLicense.previousDriverLicenseNumber?.trim(),
        userActionIndicator:
          allLicenseData.employeeDriverLicense.userActionIndicator,
        createdDate:
          allLicenseData.employeeDriverLicense.createdDate?.trim() || '',
        expiryDate: this.stringToDate(
          allLicenseData.employeeDriverLicense.expiryDate?.trim()
        ),
        dateOfBirth: this.stringToDate(
          allLicenseData.employeeDriverLicense.dateOfBirth?.trim()
        ),
        consentUploadedTimestamp:
          allLicenseData.employeeDriverLicense.consentUploadedTimestamp?.trim() ||
          '',
        modTimeStampUtc:
          allLicenseData.employeeDriverLicense.modTimeStampUtc?.trim() || '',
        lastConsentedTimeStamp:
          this.formatLastConsentedTimestamp(
            allLicenseData.employeeDriverLicense.lastConsentedTimeStamp?.trim()
          ) || '',
        forcedDLMessage:
          allLicenseData.employeeDriverLicense.forcedDLMessage?.trim() || '',

        authorisedUserInd: this.yesNoToBoolean(
          allLicenseData.employeeDriverLicense.authorisedUserInd?.trim()
        ),
        mcEndorsementInd: this.yesNoToBoolean(
          allLicenseData.employeeDriverLicense.mcEndorsementInd?.trim()
        ),
        activeInd: this.yesNoToBoolean(
          allLicenseData.employeeDriverLicense.activeInd?.trim()
        ),
        primaryDriverInd: this.yesNoToBoolean(
          allLicenseData.employeeDriverLicense.primaryDriverInd?.trim()
        ),
        consentInd: this.yesNoToBoolean(
          allLicenseData.employeeDriverLicense.consentInd?.trim()
        ),
        forcedDLUpdateInd: this.yesNoToBoolean(
          allLicenseData.employeeDriverLicense.forcedDLUpdateInd?.trim()
        ),
        deactivateInd: this.yesNoToBoolean(
          allLicenseData.employeeDriverLicense.deactivateInd?.trim()
        ),
        // under18Indicator: this.yesNoToBoolean(
        //   allLicenseData.employeeDriverLicense.under18Indicator?.trim()
        // ),
        previousConsentInd: this.yesNoToBoolean(
          allLicenseData.employeeDriverLicense.previousConsentInd?.trim()
        ),
      },
      inactiveAuthorisedUserDriverLicense: [],
    };

    allLicenseData.inactiveAuthorisedUserDriverLicense?.forEach(
      (inactiveAuthorisedDriverLicense) => {
        sanitizedAllLicenseData.inactiveAuthorisedUserDriverLicense.push({
          _isExistingData: inactiveAuthorisedDriverLicense.customPersonId
            ? true
            : false,
          _id: this.generateRandomUUID(),
          employeeId: inactiveAuthorisedDriverLicense.employeeId?.trim(),
          driverLicenseNumber:
            inactiveAuthorisedDriverLicense.driverLicenseNumber?.trim(),
          countryCode: inactiveAuthorisedDriverLicense.countryCode?.trim(),
          stateProvinceCode:
            inactiveAuthorisedDriverLicense.stateProvinceCode?.trim(),
          firstName: inactiveAuthorisedDriverLicense.firstName?.trim(),
          middleName: inactiveAuthorisedDriverLicense.middleName?.trim(),
          lastName: inactiveAuthorisedDriverLicense.lastName?.trim(),
          suffix: inactiveAuthorisedDriverLicense.suffix?.trim(),
          last4DigitsSSN:
            inactiveAuthorisedDriverLicense.last4DigitsSSN?.trim(),
          zipCode: inactiveAuthorisedDriverLicense.zipCode?.trim(),
          driverLicenseClass:
            inactiveAuthorisedDriverLicense.driverLicenseClass?.trim(),
          addressLine1: inactiveAuthorisedDriverLicense.addressLine1?.trim(),
          addressLine2: inactiveAuthorisedDriverLicense.addressLine2?.trim(),
          city: inactiveAuthorisedDriverLicense.city?.trim(),
          createdByUser: inactiveAuthorisedDriverLicense.createdByUser?.trim(),
          modUser: inactiveAuthorisedDriverLicense.modUser?.trim(),
          email: inactiveAuthorisedDriverLicense.email?.trim(),
          orderMvrStatus:
            inactiveAuthorisedDriverLicense.orderMvrStatus?.trim(),
          previousStateProvinceCode:
            inactiveAuthorisedDriverLicense.previousStateProvinceCode?.trim(),
          relationToEmployee:
            inactiveAuthorisedDriverLicense.relationToEmployee?.trim(),
          customPersonId:
            inactiveAuthorisedDriverLicense.customPersonId?.trim(),
          hrCompany: inactiveAuthorisedDriverLicense.hrCompany?.trim(),
          previousDriverLicenseNumber:
            inactiveAuthorisedDriverLicense.previousDriverLicenseNumber?.trim(),
          userActionIndicator:
            inactiveAuthorisedDriverLicense.userActionIndicator,
          createdDate: inactiveAuthorisedDriverLicense.createdDate?.trim(),
          expiryDate: this.stringToDate(
            inactiveAuthorisedDriverLicense.expiryDate?.trim()
          ),
          dateOfBirth: this.stringToDate(
            inactiveAuthorisedDriverLicense.dateOfBirth?.trim()
          ),
          consentUploadedTimestamp:
            inactiveAuthorisedDriverLicense.consentUploadedTimestamp?.trim() ||
            '',
          modTimeStampUtc:
            inactiveAuthorisedDriverLicense.modTimeStampUtc?.trim() || '',
          lastConsentedTimeStamp:
            inactiveAuthorisedDriverLicense.lastConsentedTimeStamp?.trim() ||
            '',
          forcedDLMessage:
            inactiveAuthorisedDriverLicense.forcedDLMessage?.trim() || '',

          authorisedUserInd: this.yesNoToBoolean(
            inactiveAuthorisedDriverLicense.authorisedUserInd?.trim()
          ),
          mcEndorsementInd: this.yesNoToBoolean(
            inactiveAuthorisedDriverLicense.mcEndorsementInd?.trim()
          ),
          activeInd: this.yesNoToBoolean(
            inactiveAuthorisedDriverLicense.activeInd?.trim()
          ),
          primaryDriverInd: this.yesNoToBoolean(
            inactiveAuthorisedDriverLicense.primaryDriverInd?.trim()
          ),
          consentInd: this.yesNoToBoolean(
            inactiveAuthorisedDriverLicense.consentInd?.trim()
          ),
          forcedDLUpdateInd: this.yesNoToBoolean(
            inactiveAuthorisedDriverLicense.forcedDLUpdateInd?.trim()
          ),
          deactivateInd: this.yesNoToBoolean(
            inactiveAuthorisedDriverLicense.deactivateInd?.trim()
          ),
          under18Indicator: this.yesNoToBoolean(
            inactiveAuthorisedDriverLicense.under18Indicator?.trim() || 'N'
          ),
          previousConsentInd: this.yesNoToBoolean(
            inactiveAuthorisedDriverLicense.previousConsentInd?.trim()
          ),
        });
      }
    );

    allLicenseData.authorisedUserDriverLicense?.forEach(
      (authorisedDriverLicense) => {
        sanitizedAllLicenseData.authorisedUserDriverLicense.push({
          _isExistingData: authorisedDriverLicense.customPersonId?.trim()
            ? true
            : false,
          _id: this.generateRandomUUID(),
          employeeId: authorisedDriverLicense.employeeId?.trim(),
          driverLicenseNumber:
            authorisedDriverLicense.driverLicenseNumber?.trim(),
          countryCode: authorisedDriverLicense.countryCode?.trim(),
          stateProvinceCode: authorisedDriverLicense.stateProvinceCode?.trim(),
          firstName: authorisedDriverLicense.firstName?.trim(),
          middleName: authorisedDriverLicense.middleName?.trim(),
          lastName: authorisedDriverLicense.lastName?.trim(),
          suffix: authorisedDriverLicense.suffix?.trim(),
          last4DigitsSSN: authorisedDriverLicense.last4DigitsSSN?.trim(),
          zipCode: authorisedDriverLicense.zipCode?.trim(),
          driverLicenseClass:
            authorisedDriverLicense.driverLicenseClass?.trim(),
          addressLine1: authorisedDriverLicense.addressLine1?.trim(),
          addressLine2: authorisedDriverLicense.addressLine2?.trim(),
          city: authorisedDriverLicense.city?.trim(),
          createdByUser: authorisedDriverLicense.createdByUser?.trim(),
          modUser: authorisedDriverLicense.modUser?.trim(),
          email: authorisedDriverLicense.email?.trim(),
          orderMvrStatus: authorisedDriverLicense.orderMvrStatus?.trim(),
          previousStateProvinceCode:
            authorisedDriverLicense.previousStateProvinceCode?.trim(),
          relationToEmployee:
            authorisedDriverLicense.relationToEmployee?.trim(),
          customPersonId: authorisedDriverLicense.customPersonId?.trim(),
          hrCompany: authorisedDriverLicense.hrCompany?.trim(),
          previousDriverLicenseNumber:
            authorisedDriverLicense.previousDriverLicenseNumber?.trim(),
          userActionIndicator: authorisedDriverLicense.userActionIndicator,
          createdDate: authorisedDriverLicense.createdDate?.trim() || '',

          expiryDate: this.stringToDate(
            authorisedDriverLicense.expiryDate?.trim()
          ),
          dateOfBirth: this.stringToDate(
            authorisedDriverLicense.dateOfBirth?.trim()
          ),
          consentUploadedTimestamp:
            authorisedDriverLicense.consentUploadedTimestamp?.trim() || '',

          modTimeStampUtc:
            authorisedDriverLicense.modTimeStampUtc?.trim() || '',

          lastConsentedTimeStamp:
            this.formatLastConsentedTimestamp(
              authorisedDriverLicense.lastConsentedTimeStamp?.trim()
            ) || '',
          // lastConsentedTimeStamp:
          //   authorisedDriverLicense.lastConsentedTimeStamp?.trim() || '',
          forcedDLMessage:
            authorisedDriverLicense.forcedDLMessage?.trim() || '',

          authorisedUserInd: this.yesNoToBoolean(
            authorisedDriverLicense.authorisedUserInd?.trim()
          ),
          mcEndorsementInd: this.yesNoToBoolean(
            authorisedDriverLicense.mcEndorsementInd?.trim()
          ),
          activeInd: this.yesNoToBoolean(
            authorisedDriverLicense.activeInd?.trim()
          ),
          primaryDriverInd: this.yesNoToBoolean(
            authorisedDriverLicense.primaryDriverInd?.trim()
          ),
          consentInd: this.yesNoToBoolean(
            authorisedDriverLicense.consentInd?.trim()
          ),
          forcedDLUpdateInd: this.yesNoToBoolean(
            authorisedDriverLicense.forcedDLUpdateInd?.trim()
          ),
          deactivateInd: this.yesNoToBoolean(
            authorisedDriverLicense.deactivateInd?.trim()
          ),
          under18Indicator: this.yesNoToBoolean(
            authorisedDriverLicense.under18Indicator?.trim() || 'N'
          ),
          previousConsentInd: this.yesNoToBoolean(
            authorisedDriverLicense.previousConsentInd?.trim()
          ),
        });
      }
    );

    return sanitizedAllLicenseData;
  }

  async handleReset() {
    this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    this.allLicenseFormSubscriptionMap.forEach((subscrption) =>
      subscrption.unsubscribe()
    );
    this.allLicenseFormSubscriptionMap.clear();
    await this.ngOnInit();
    this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
  }

  handleRefresh() {
    window.location.reload();
  }

  getFileSize(fileInBase64String: string) {
    let fileSize = '';
    try {
      if (!fileInBase64String) {
        fileSize = '0 KB';
      }

      const base64Data = fileInBase64String.split(',')[1] || fileInBase64String;
      const base64Length = base64Data.length;
      const approximateBytes = base64Length * 0.75;
      fileSize =
        (Math.round(approximateBytes / 1024).toString() || '0') + ' KB';
    } catch {
      fileSize = '0 KB';
    }
    return fileSize;
  }

  async handleFileSelected() {
    try {
      this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
    }
  }

  async convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        const base64String = reader.result as string;
        resolve(base64String);
      };

      reader.onerror = () => {
        reject('');
      };
    });
  }

  handleError(error: unknown) {
    if (error instanceof HttpErrorResponse) {
      if (error.error?.status === 'FAILURE') {
        this.toastService.pushToast({
          id: 'bad-request-error-toast',
          tone: 'critical',
          toastText:
            this.commonBackendResponseHandlerService.generateErrorMessageForEmployeeAndAuthorizedDriverScreen(
              error.error.message
            ) || 'Please add / update unique valid data.',
        } as DsToastModel);
        return;
      } else if (error.error?.status === 'ERROR') {
        this.toastService.pushToast({
          id: 'server-error-toast',
          tone: 'critical',
          toastText:
            this.commonBackendResponseHandlerService.getErrorMessageFromErrorCode(
              error.error.message
            ) || 'Unable to process your request. Please try again.',
        } as DsToastModel);
        return;
      } else if (error.status === 401 || error.status === 403) {
        this.toastService.pushToast({
          id: 'auth-error-toast',
          tone: 'critical',
          toastText: 'Please check credentials and try again.',
        } as DsToastModel);
        return;
      } else if (error.status > 400 && error.status < 500) {
        this.toastService.pushToast({
          id: 'bad-request-error-toast',
          tone: 'critical',
          toastText: 'Please check submitted data and try again.',
        } as DsToastModel);
        return;
      } else if (error.status >= 500) {
        this.toastService.pushToast({
          id: 'server-error-toast',
          tone: 'critical',
          toastText: 'Unable to process your request. Please try again.',
        } as DsToastModel);
        return;
      } else {
        this.toastService.pushToast({
          id: 'error-toast',
          tone: 'critical',
          toastText: 'Some error occured. Please try again.',
        } as DsToastModel);
        return;
      }
    } else if (error instanceof Error && error.message) {
      this.toastService.pushToast({
        id: 'error-toast',
        tone: 'critical',
        toastText: error.message,
      } as DsToastModel);
      return;
    } else {
      this.toastService.pushToast({
        id: 'error-toast',
        tone: 'critical',
        toastText: 'Some error occured. Please try again.',
      } as DsToastModel);
      return;
    }
  }

  async openLinkInNewTab(documentType: string) {
    this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    try {
      let backendResponse: EmployeeDriverLicenseAndInformationFileResponseData;
      let urlToOpen: string = '';
      switch (documentType) {
        case 'enrollGuideDocument':
          backendResponse = await firstValueFrom(
            this.backendService.getHowToEnrollGuideDocument()
          );
          urlToOpen = window.URL.createObjectURL(
            new Blob(
              [
                new Uint8Array(
                  atob(backendResponse?.base64EncodedContent)
                    .split('')
                    .map((char) => char.charCodeAt(0))
                ),
              ],
              { type: 'application/pdf' }
            )
          );
          window.open(urlToOpen, '_blank');
          break;
        case 'summaryOfRightsDocument':
          backendResponse = await firstValueFrom(
            this.backendService.getSummaryOfRightsDocument()
          );
          urlToOpen = window.URL.createObjectURL(
            new Blob(
              [
                new Uint8Array(
                  atob(backendResponse?.base64EncodedContent)
                    .split('')
                    .map((char) => char.charCodeAt(0))
                ),
              ],
              { type: 'application/pdf' }
            )
          );
          window.open(urlToOpen, '_blank');
          break;
        case 'carPolicyDocument':
          window.open(this.policyDocumentUrl, '_blank');
          break;
        default:
          break;
      }
    } catch (error) {
      this.handleError(error);
    } finally {
      this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
    }
  }

  handleAddAuthorizedDriver() {
    const newAuthorizedDriverForm = this.generateAuthorizedDriverForm();
    newAuthorizedDriverForm.patchValue({
      _id: this.generateRandomUUID(),
      employeeId: this.userProfile?.employeeId,
      userActionIndicator: 'A',
      modUser: this.userProfile?.qxid,
      authorisedUserInd: true,
      activeInd: true,
      hrCompany: this.userProfile?.companyCode,

      addressLine1: '',
      city: '',
      countryCode: '',
      createdByUser: '',
      createdDate: '',
      customPersonId: '',
      driverLicenseClass: '',
      driverLicenseNumber: '',
      forcedDLMessage: '',
      last4DigitsSSN: '',
      lastConsentedTimeStamp: '',
      orderMvrStatus: '',
      previousDriverLicenseNumber: '',
      previousStateProvinceCode: '',
      relationToEmployee: '',
      stateProvinceCode: '',
      zipCode: '',
    });
    this.authorizedDriverForms.push(newAuthorizedDriverForm);
    this.mapFormWithSubscription(newAuthorizedDriverForm);
  }

  handleModalClosed() {
    // this.handleError(new Error('Please agree the consent to proceed.'));
    this.handleResetModal();
  }

  handleDeleteAuthorizedDriverModal(formGroup: FormGroup) {
    this.showModal('deleteAuthorizedDriver', formGroup);
  }

  async handleDeleteAuthorizedDriver(formGroup?: FormGroup) {
    try {
      // confirm modal
      this.handleModalConfirmation();
      if (!formGroup) {
        return;
      }

      // check if existing data
      const isExistingData = formGroup.get('_isExistingData')?.value;

      if (!isExistingData) {
        const toBeDeletedIndex = this.authorizedDriverForms.findIndex(
          (formGroupToBeDeleted) => {
            return (
              formGroupToBeDeleted.getRawValue()._id ===
              formGroup.getRawValue()._id
            );
          }
        );

        this.authorizedDriverForms.splice(toBeDeletedIndex, 1);

        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'positive',
          toastText: 'Data deleted.',
        } as DsToastModel);
        return;
      }

      // validate data
      const formHasInvalidData = this.checkInvalidDataInForm(
        formGroup,
        true,
        false
      );
      if (formHasInvalidData.length !== 0) {
        this.toastService.pushToast({
          id: 'warning-toast',
          tone: 'caution',
          toastText: formHasInvalidData || 'Please enter valid data.',
        } as DsToastModel);
        return;
      }

      // sanitize data
      let sanitizedAuthorizedDriverFormData = this.sanitizeForm(formGroup);
      sanitizedAuthorizedDriverFormData =
        this.masterAllLicenseData.authorisedUserDriverLicense.filter((data) => {
          return (
            data.customPersonId ===
            sanitizedAuthorizedDriverFormData.customPersonId
          );
        })[0];
      sanitizedAuthorizedDriverFormData.deactivateInd = 'Y';
      sanitizedAuthorizedDriverFormData.userActionIndicator = 'U';
      sanitizedAuthorizedDriverFormData.consentUploadedTimestamp = '';

      const sanitizedEmployeeFormData = this.sanitizeForm(this.employeeForm);

      const payloadForDeleteAuthorizedDriverLicense: SaveDriverLicenseRequestData =
        {
          employeeDriverLicense: sanitizedEmployeeFormData,
          authorisedUserDriverLicense: [sanitizedAuthorizedDriverFormData],
        };

      this.sharedService.logger(
        'Employee DL and info, payload data for deleting authorized driver form ',
        payloadForDeleteAuthorizedDriverLicense
      );

      // backend call to delete data
      this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);

      const deleteAuthorizedDriverResult = await firstValueFrom(
        this.backendService.saveDriverLicenseData(
          payloadForDeleteAuthorizedDriverLicense
        )
      );

      if (deleteAuthorizedDriverResult?.status === 'NO_CONTENT') {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'caution',
          toastText: 'There are no new changes, please add / update data.',
        } as DsToastModel);
      } else if (deleteAuthorizedDriverResult?.status === 'SUCCESS') {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'positive',
          toastText:
            this.commonBackendResponseHandlerService.generateSuccessMessageForEmployeeAndAuthorizedDriverScreen(
              deleteAuthorizedDriverResult?.message as CommonBackendResponse
            ) || 'Data deleted.',
        } as DsToastModel);
      } else if (deleteAuthorizedDriverResult?.status === 'PARTIAL_SUCCESS') {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'caution',
          toastText:
            this.commonBackendResponseHandlerService.generatePartialSuccessMessageForEmployeeAndAuthorizedDriverScreen(
              deleteAuthorizedDriverResult?.message as CommonBackendResponse
            ) || 'Data deleted.',
        } as DsToastModel);
      } else {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'positive',
          toastText: 'Data deleted.',
        } as DsToastModel);
      }

      // reset forms
      await this.handleReset();
    } catch (error) {
      this.handleError(error);
    } finally {
      this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
    }
  }

  handleSubmitEmployeeFormModal(formGroup: FormGroup) {
    // check for actual changes in form
    if (!formGroup.dirty) {
      this.toastService.pushToast({
        id: 'success-toast',
        tone: 'caution',
        toastText: 'There are no new changes, please add / update data.',
      } as DsToastModel);
      return;
    }

    // validate data
    const formHasInvalidData = this.checkInvalidDataInForm(formGroup);
    if (formHasInvalidData.length !== 0) {
      this.toastService.pushToast({
        id: 'warning-toast',
        tone: 'caution',
        toastText: formHasInvalidData || 'Please enter valid data.',
      } as DsToastModel);
      return;
    }

    this.showModal('submitEmployeeForm', formGroup);
  }

  async handleSubmitEmployeeForm(formGroup?: FormGroup) {
    try {
      if (!formGroup) {
        return;
      }

      // check for actual changes in form
      if (!formGroup.dirty) {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'caution',
          toastText: 'There are no new changes, please add / update data.',
        } as DsToastModel);
        return;
      }

      // confirm modal
      this.handleModalConfirmation();

      // // validate data
      // const formHasInvalidData = this.checkInvalidDataInForm(formGroup);
      // if (formHasInvalidData.length !== 0) {
      //   this.toastService.pushToast({
      //     id: 'warning-toast',
      //     tone: 'caution',
      //     toastText: formHasInvalidData || 'Please enter valid data.',
      //   } as DsToastModel);
      //   return;
      // }

      // sanitize data

      const sanitizedEmployeeFormData = this.sanitizeForm(this.employeeForm);
      sanitizedEmployeeFormData.userActionIndicator = 'U';
      sanitizedEmployeeFormData.consentInd = 'Y';

      // check if existing data
      const isExistingData = formGroup.get('_isExistingData')?.value;

      if (!isExistingData) {
        sanitizedEmployeeFormData.userActionIndicator = 'A';
      }

      const payloadForSubmitEmployeeDriverLicense: SaveDriverLicenseRequestData =
        {
          employeeDriverLicense: sanitizedEmployeeFormData,
        };

      this.sharedService.logger(
        'Employee DL and info, payload data for submitting employee form ',
        payloadForSubmitEmployeeDriverLicense
      );

      // backend call to submit data
      this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);

      const submitEmployeeResult = await firstValueFrom(
        this.backendService.saveDriverLicenseData(
          payloadForSubmitEmployeeDriverLicense
        )
      );

      if (submitEmployeeResult?.status === 'NO_CONTENT') {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'caution',
          toastText: 'There are no new changes, please add / update data.',
        } as DsToastModel);
      } else if (submitEmployeeResult?.status === 'SUCCESS') {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'positive',
          toastText:
            this.commonBackendResponseHandlerService.generateSuccessMessageForEmployeeAndAuthorizedDriverScreen(
              submitEmployeeResult?.message as CommonBackendResponse
            ) || 'Data deleted.',
        } as DsToastModel);
      } else if (submitEmployeeResult?.status === 'PARTIAL_SUCCESS') {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'caution',
          toastText:
            this.commonBackendResponseHandlerService.generatePartialSuccessMessageForEmployeeAndAuthorizedDriverScreen(
              submitEmployeeResult?.message as CommonBackendResponse
            ) || 'Data deleted.',
        } as DsToastModel);
      } else {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'positive',
          toastText: 'Data deleted.',
        } as DsToastModel);
      }

      // reset forms
      await this.handleReset();
    } catch (error) {
      this.handleError(error);
    } finally {
      this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
    }
  }

  handleSubmitAuthorizedDriverFormModal(formGroup: FormGroup) {
    // check for actual changes in form
    if (!formGroup.dirty) {
      this.toastService.pushToast({
        id: 'success-toast',
        tone: 'caution',
        toastText: 'There are no new changes, please add / update data.',
      } as DsToastModel);
      return;
    }

    this.showModal('submitAuthorizedDriverForm', formGroup);
  }

  async handleSubmitAuthorizedDriverForm(formGroup?: FormGroup) {
    try {
      if (!formGroup) {
        return;
      }

      // confirm modal
      this.handleModalConfirmation();

      // check for actual changes in form
      if (!formGroup.dirty) {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'caution',
          toastText: 'There are no new changes, please add / update data.',
        } as DsToastModel);
        return;
      }

      // validate data
      const formHasInvalidData = this.checkInvalidDataInForm(formGroup);
      if (formHasInvalidData.length !== 0) {
        this.toastService.pushToast({
          id: 'warning-toast',
          tone: 'caution',
          toastText: formHasInvalidData || 'Please enter valid data.',
        } as DsToastModel);
        return;
      }

      // sanitize data
      const sanitizedAuthorizedDriverFormData = this.sanitizeForm(formGroup);
      const sanitizedEmployeeFormData = this.sanitizeForm(this.employeeForm);
      if (sanitizedAuthorizedDriverFormData.userActionIndicator === 'R') {
        sanitizedAuthorizedDriverFormData.userActionIndicator = 'R';
      } else {
        sanitizedAuthorizedDriverFormData.userActionIndicator = 'U';
      }

      // check if existing data
      const isExistingData = formGroup.get('_isExistingData')?.value;

      if (!isExistingData) {
        sanitizedAuthorizedDriverFormData.userActionIndicator = 'A';
      }

      const payloadForSubmitAuthorizedDriverLicense: SaveDriverLicenseRequestData =
        {
          employeeDriverLicense: sanitizedEmployeeFormData,
          authorisedUserDriverLicense: [sanitizedAuthorizedDriverFormData],
        };

      this.sharedService.logger(
        'Employee DL and info, payload data for submitting auth driver form ',
        payloadForSubmitAuthorizedDriverLicense
      );

      const payloadForUploadConsentForAuthorizedDriver: UploadConsentForAuthorizedDriverFromEmployeeDriverLicenseRequestData =
        {
          employeeDriverLicense: {
            employeeId: sanitizedEmployeeFormData.employeeId,
            driverLicenseNumber: sanitizedEmployeeFormData.driverLicenseNumber,
            countryCode: sanitizedEmployeeFormData.countryCode,
            stateProvinceCode: sanitizedEmployeeFormData.stateProvinceCode,
            firstName: sanitizedEmployeeFormData.firstName,
            middleName: sanitizedEmployeeFormData.middleName,
            lastName: sanitizedEmployeeFormData.lastName,
            suffix: sanitizedEmployeeFormData.suffix,
            customPersonId: sanitizedEmployeeFormData.customPersonId,
            authorisedUserInd: sanitizedEmployeeFormData.authorisedUserInd,
          },
          authorisedUserDriverLicense: [
            {
              employeeId: sanitizedAuthorizedDriverFormData.employeeId,
              driverLicenseNumber:
                sanitizedAuthorizedDriverFormData.driverLicenseNumber,
              countryCode: sanitizedAuthorizedDriverFormData.countryCode,
              stateProvinceCode:
                sanitizedAuthorizedDriverFormData.stateProvinceCode,
              firstName: sanitizedAuthorizedDriverFormData.firstName,
              middleName: sanitizedAuthorizedDriverFormData.middleName,
              lastName: sanitizedAuthorizedDriverFormData.lastName,
              suffix: sanitizedAuthorizedDriverFormData.suffix,
              customPersonId: sanitizedAuthorizedDriverFormData.customPersonId,
              authorisedUserInd:
                sanitizedAuthorizedDriverFormData.authorisedUserInd,
              consentFile:
                sanitizedAuthorizedDriverFormData?.base64ConsentFile || '',
            },
          ],
        };

      // backend call to submit data
      this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);

      const uploadConsentForAuthorizedDriverResult = await firstValueFrom(
        this.backendService.uploadConsentForAuthorizedDriverFromEmployeeDriverLicense(
          payloadForUploadConsentForAuthorizedDriver
        )
      );

      if (uploadConsentForAuthorizedDriverResult?.status === 'SUCCESS') {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'positive',
          toastText:
            this.commonBackendResponseHandlerService
              .generateSuccessMessageForEmployeeAndAuthorizedDriverScreen(
                uploadConsentForAuthorizedDriverResult?.message as CommonBackendResponse
              )
              ?.replace(' data is', ' consent data is') || 'Consent updated.',
        } as DsToastModel);
      } else if (
        uploadConsentForAuthorizedDriverResult?.status === 'PARTIAL_SUCCESS'
      ) {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'caution',
          toastText:
            this.commonBackendResponseHandlerService.generatePartialSuccessMessageForEmployeeAndAuthorizedDriverScreen(
              uploadConsentForAuthorizedDriverResult?.message as CommonBackendResponse
            ) || 'Consent updated.',
        } as DsToastModel);
      } else {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'positive',
          toastText: 'Consent updated.',
        } as DsToastModel);
      }

      if (
        payloadForSubmitAuthorizedDriverLicense?.authorisedUserDriverLicense &&
        payloadForSubmitAuthorizedDriverLicense?.authorisedUserDriverLicense &&
        uploadConsentForAuthorizedDriverResult &&
        uploadConsentForAuthorizedDriverResult?.message
      ) {
        payloadForSubmitAuthorizedDriverLicense.authorisedUserDriverLicense[0].consentUploadedTimestamp =
          uploadConsentForAuthorizedDriverResult?.message[0]
            .consentUploadedTimestamp || '';

        payloadForSubmitAuthorizedDriverLicense.authorisedUserDriverLicense[0].consentInd =
          'Y';
        payloadForSubmitAuthorizedDriverLicense.authorisedUserDriverLicense[0].consentFile =
          '';
        payloadForSubmitAuthorizedDriverLicense.authorisedUserDriverLicense[0].base64ConsentFile =
          '';
      }

      const submitAuthorizedDriverResult = await firstValueFrom(
        this.backendService.saveDriverLicenseData(
          payloadForSubmitAuthorizedDriverLicense
        )
      );

      if (submitAuthorizedDriverResult?.status === 'NO_CONTENT') {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'caution',
          toastText: 'There are no new changes, please add / update data.',
        } as DsToastModel);
      } else if (submitAuthorizedDriverResult?.status === 'SUCCESS') {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'positive',
          toastText:
            this.commonBackendResponseHandlerService.generateSuccessMessageForEmployeeAndAuthorizedDriverScreen(
              submitAuthorizedDriverResult?.message as CommonBackendResponse
            ) || 'Data saved.',
        } as DsToastModel);
      } else if (submitAuthorizedDriverResult?.status === 'PARTIAL_SUCCESS') {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'caution',
          toastText:
            this.commonBackendResponseHandlerService.generatePartialSuccessMessageForEmployeeAndAuthorizedDriverScreen(
              submitAuthorizedDriverResult?.message as CommonBackendResponse
            ) || 'Data saved.',
        } as DsToastModel);
      } else {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'positive',
          toastText: 'Data saved.',
        } as DsToastModel);
      }

      // reset forms
      await this.handleReset();
    } catch (error) {
      this.handleError(error);
    } finally {
      this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
    }
  }

  async handleSaveAuthorizedDriverInTempTable(formGroup: FormGroup) {
    try {
      if (!formGroup) {
        return;
      }
      //  check for actual changes in form
      if (!formGroup.dirty) {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'caution',
          toastText: 'There are no new changes, please add / update data.',
        } as DsToastModel);
        // return;
      }

      // validate data
      const formHasInvalidData = this.checkInvalidDataInForm(formGroup);
      if (formHasInvalidData.length !== 0) {
        this.toastService.pushToast({
          id: 'warning-toast',
          tone: 'caution',
          toastText: formHasInvalidData || 'Please enter valid data.',
        } as DsToastModel);
        return;
      }

      // sanitize data
      const sanitizedAuthorizedDriverFormData = this.sanitizeForm(formGroup);
      const sanitizedEmployeeFormData = this.sanitizeForm(this.employeeForm);
      sanitizedAuthorizedDriverFormData.userActionIndicator = 'U';
      sanitizedAuthorizedDriverFormData.consentInd = 'N';
      sanitizedAuthorizedDriverFormData.consentUploadedTimestamp = '';

      // check if existing data
      const isExistingData = formGroup.get('_isExistingData')?.value;

      if (!isExistingData) {
        sanitizedAuthorizedDriverFormData.userActionIndicator = 'A';
      }

      this.sharedService.logger(
        'Employee DL and info, sanitized data for saving authorized driver form in temp table',
        sanitizedAuthorizedDriverFormData
      );

      const payloadForSaveAuthorizedDriverLicenseInTempTable: SaveDriverLicenseInTempTableLicenseRequestData =
        {
          employeeDriverLicense: null,
          authorisedUserDriverLicense: [sanitizedAuthorizedDriverFormData],
        };

      this.sharedService.logger(
        'Employee DL and info, payload data for saving authorized driver form ',
        payloadForSaveAuthorizedDriverLicenseInTempTable
      );

      // backend call to save data
      this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);

      const saveDriverLicenseDataInTempTableResult = await firstValueFrom(
        this.backendService.saveDriverLicenseDataInTempTable(
          payloadForSaveAuthorizedDriverLicenseInTempTable
        )
      );
      if (saveDriverLicenseDataInTempTableResult?.status === 'NO_CONTENT') {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'caution',
          toastText: 'There are no new changes, please add / update data.',
        } as DsToastModel);
      } else if (saveDriverLicenseDataInTempTableResult?.status === 'SUCCESS') {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'positive',
          toastText:
            this.commonBackendResponseHandlerService.generateSuccessMessageForEmployeeAndAuthorizedDriverScreen(
              saveDriverLicenseDataInTempTableResult?.message as CommonBackendResponse
            ) || 'Data saved.',
        } as DsToastModel);
      } else if (
        saveDriverLicenseDataInTempTableResult?.status === 'PARTIAL_SUCCESS'
      ) {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'caution',
          toastText:
            this.commonBackendResponseHandlerService.generatePartialSuccessMessageForEmployeeAndAuthorizedDriverScreen(
              saveDriverLicenseDataInTempTableResult?.message as CommonBackendResponse
            ) || 'Data saved.',
        } as DsToastModel);
      } else {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'positive',
          toastText: 'Data saved.',
        } as DsToastModel);
      }

      const relationToEmployee =
        this.allRelationshipData.filter((data) => {
          return (
            sanitizedAuthorizedDriverFormData.relationToEmployee ===
            data.relationshipCode
          );
        })[0].relationToEmployee || '';

      // generate consent PDF
      const generatedConsentHTML = this.consentPDFService.generateHTML(
        sanitizedAuthorizedDriverFormData,
        sanitizedEmployeeFormData,
        this.userProfile?.companyCode || '',
        this.employeeForm.get('stateProvinceCode')?.value || '',
        sanitizedAuthorizedDriverFormData.stateProvinceCode,
        relationToEmployee
      );

      const generatedConsentFile = await this.consentPDFService.getConsentPDF(
        generatedConsentHTML,
        sanitizedAuthorizedDriverFormData,
        sanitizedEmployeeFormData,
        this.userProfile?.companyCode || '',
        this.employeeForm.get('stateProvinceCode')?.value || '',
        sanitizedAuthorizedDriverFormData.stateProvinceCode,
        relationToEmployee
      );

      if (
        !generatedConsentFile?.base64File ||
        !generatedConsentFile?.generatedHTML
      ) {
        throw Error('Unable to generate consent PDF.');
      }

      // backend call to email user
      const payloadForEmailAuthorziedDriverLicenseInTempTable: EmailConsentToAuthorizedDriverRequestData =
        {
          employeeName: `${this.employeeForm.get('firstName')?.value} ${this.employeeForm.get('middleName')?.value} ${this.employeeForm.get('lastName')?.value}`,
          employeeEmailId: this.employeeForm.get('email')?.value || '',
          authorizedUserEmailId: sanitizedAuthorizedDriverFormData.email,
          authorizedUserName: `${sanitizedAuthorizedDriverFormData.firstName} ${sanitizedAuthorizedDriverFormData.middleName} ${sanitizedAuthorizedDriverFormData.lastName}`,
          base64EncodedContent: generatedConsentFile.base64File,
          fileName: `${this.employeeForm.get('lastName')?.value}_${this.employeeForm.get('firstName')?.value}_${sanitizedAuthorizedDriverFormData.lastName}_${sanitizedAuthorizedDriverFormData.firstName}_consent_document.pdf`,
        };

      this.sharedService.logger(
        'Employee DL and info, payload data for email authorized driver form ',
        payloadForSaveAuthorizedDriverLicenseInTempTable
      );

      const emailConsentToAuthorizedDriverResult = await firstValueFrom(
        this.backendService.emailConsentToAuthorizedDriver(
          payloadForEmailAuthorziedDriverLicenseInTempTable
        )
      );

      if (emailConsentToAuthorizedDriverResult.status === 'SUCCESS') {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'positive',
          toastText: `Email sent.`,
        } as DsToastModel);
      }

      formGroup
        .get('base64ConsentFile')
        ?.patchValue(generatedConsentFile.generatedHTML);
      this.showModal('emailConsentToAuthorizedDriver', formGroup);

      // reset forms
      await this.handleReset();
    } catch (error) {
      this.handleError(error);
    } finally {
      this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
    }
  }

  async handleReactivateAuthorizedDriver(formGroup: FormGroup) {
    try {
      // validate data
      // sanitize data
      const sanitizedDeactivatedAuthorizedDriverFormData =
        this.sanitizeForm(formGroup);
      sanitizedDeactivatedAuthorizedDriverFormData.userActionIndicator = 'R';

      const sanitizedEmployeeFormData = this.sanitizeForm(this.employeeForm);

      this.sharedService.logger(
        'Employee DL and info, sanitized data for reactivating authorized driver form ',
        sanitizedDeactivatedAuthorizedDriverFormData
      );

      const payloadForReactivateAuthorizedDriverLicenseInTempTable: ReactivateAuthorizedDriverRequestData =
        {
          employeeDriverLicense: sanitizedEmployeeFormData,
          inactiveAuthorisedUserDriverLicense: [
            sanitizedDeactivatedAuthorizedDriverFormData,
          ],
        };

      this.sharedService.logger(
        'Employee DL and info, payload data for reactivating authorized driver form ',
        payloadForReactivateAuthorizedDriverLicenseInTempTable
      );

      // backend call to reactivate data
      this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);

      const reactivateAuthorizedDriverResult = await firstValueFrom(
        this.backendService.reactivateAuthorizedDriver(
          payloadForReactivateAuthorizedDriverLicenseInTempTable
        )
      );
      if (reactivateAuthorizedDriverResult?.status === 'NO_CONTENT') {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'caution',
          toastText: 'There are no new changes, please add / update data.',
        } as DsToastModel);
      } else if (reactivateAuthorizedDriverResult?.status === 'SUCCESS') {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'positive',
          toastText:
            this.commonBackendResponseHandlerService.generateSuccessMessageForEmployeeAndAuthorizedDriverScreen(
              reactivateAuthorizedDriverResult?.message as CommonBackendResponse
            ) || 'Data saved.',
        } as DsToastModel);
      } else if (
        reactivateAuthorizedDriverResult?.status === 'PARTIAL_SUCCESS'
      ) {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'caution',
          toastText:
            this.commonBackendResponseHandlerService.generatePartialSuccessMessageForEmployeeAndAuthorizedDriverScreen(
              reactivateAuthorizedDriverResult?.message as CommonBackendResponse
            ) || 'Data saved.',
        } as DsToastModel);
      } else {
        this.toastService.pushToast({
          id: 'success-toast',
          tone: 'positive',
          toastText: 'Data saved.',
        } as DsToastModel);
      }

      // reset forms
      await this.handleReset();
    } catch (error) {
      this.handleError(error);
    } finally {
      this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
    }
  }

  yesNoToBoolean(value: string) {
    return value === 'Y' ? true : false;
  }

  booleanToYesNo(value: boolean) {
    return value ? 'Y' : 'N';
  }

  formatLastConsentedTimestamp(value: string) {
    try {
      const sanitizedValue = new Date(value);
      if (
        !(sanitizedValue instanceof Date) ||
        isNaN(sanitizedValue.getTime())
      ) {
        return '';
      }
      return subHours(new Date(value.trim()), 4)?.toLocaleString();
    } catch {
      return '';
    }
  }

  stringToDate(value: string) {
    try {
      const sanitizedValue = new Date(value);
      if (
        !(sanitizedValue instanceof Date) ||
        isNaN(sanitizedValue.getTime())
      ) {
        return null;
      }
      return new Date(value);
    } catch {
      return null;
    }
  }

  convertDateToMMDDYYYY(date: string): string {
    const formattedDate = new Date(date);
    if (!isValid(formattedDate)) return '';
    return format(formattedDate, 'MM/dd/yyyy');
  }

  getStatesForSelectedCountryInForm(countryCode: string): SelectListOption[] {
    if (!countryCode) return [];

    const activeCountry = this.allCountriesData.filter((data) => {
      return data.countryCode === countryCode && data.activeInd === 'Y';
    })[0];

    if (!activeCountry) return [];

    const countryStateCodes = this.allCountryStateCodeData
      .filter((data) => {
        return data.countryCode === activeCountry.countryCode;
      })
      .map((data) => {
        return data.stateProvinceCode;
      });

    if (!countryStateCodes) return [];

    const activeStateCodes = this.allStatesData
      .filter((data) => {
        return data.activeInd === 'Y';
      })
      .map((data) => {
        return data.stateProvinceCode;
      });

    if (!activeStateCodes) return [];

    const allowedStateCodes = countryStateCodes
      .filter((data) => {
        return activeStateCodes.includes(data);
      })
      .filter((data, index, array) => {
        return array.indexOf(data) === index;
      });

    if (!allowedStateCodes) return [];

    return this.allStatesData
      .filter((data) => {
        return allowedStateCodes.includes(data.stateProvinceCode);
      })
      .map((data) => {
        return {
          id: `${data.stateProvinceCode}`,
          label: `${data.stateDescription}`,
        };
      });
  }

  today = new Date();
  baseDatepickerConfig: Partial<DsDatepickerConfiguration> = {
    locale: enUS,
    showClearAll: false,
    allowDirectInput: true,
    disabledDatesFactory: [],
    showButtonBar: false,
    showWeekNumber: false,
    closeOnDateSelect: true,
    format: 'MM/dd/yyyy',
  };

  getDateConfigForBirthDate() {
    return {
      ...this.baseDatepickerConfig,
      // disabledDatesFactory: minMaxDate({ max: new Date() }),
    };
  }

  getDateConfigForExpiryDate() {
    return {
      ...this.baseDatepickerConfig,
      // disabledDatesFactory: minMaxDate({ min: new Date() }),
    };
  }

  getCountriesOption(): SelectListOption[] {
    return this.allCountriesData.map((data) => {
      return {
        id: `${data.countryCode}`,
        label: `${data.countryCode} - ${data.countryDescription}`,
      };
    });
  }

  getStatesOption(): SelectListOption[] {
    return this.allStatesData.map((data) => {
      return {
        id: `${data.stateProvinceCode}`,
        label: `${data.stateProvinceCode} - ${data.stateDescription}`,
      };
    });
  }

  getRelationshipsOption(formGroup?: FormGroup): SelectListOption[] {
    return this.allRelationshipData
      .filter((data) => {
        return (
          data.activeIndicator === 'Y' ||
          data.relationshipCode === formGroup?.getRawValue()?.relationToEmployee
        );
      })
      .map((data) => {
        return {
          id: `${data.relationshipCode}`,

          label: `${data.relationshipCode} - ${data.relationToEmployee}`,
        };
      });
  }

  isSSNRequired(countryCode: string, stateProvinceCode: string): boolean {
    return (
      this.yesNoToBoolean(
        this.allCountryStateCodeData.filter((data) => {
          return (
            data.countryCode === countryCode &&
            data.stateProvinceCode === stateProvinceCode
          );
        })[0]?.ssnExemptInd
      ) || false
    );
  }

  isUnder18(dateOfBirth: string | Date): boolean {
    if (!dateOfBirth) return false;

    const sanitizedDateOfBirth =
      dateOfBirth instanceof Date ? dateOfBirth : new Date(dateOfBirth);
    if (isNaN(sanitizedDateOfBirth.getTime())) return false;
    const after18Years = addYears(sanitizedDateOfBirth, 18);
    return isBefore(new Date(), after18Years);
  }

  getFormAccent(formGroup: FormGroup): BoxSurfaceTone {
    const formHasErrors =
      formGroup.get('forcedDLUpdateInd')?.getRawValue() ||
      formGroup.get('forcedDLMessage')?.getRawValue().length !== 0 ||
      !formGroup.valid;

    return formHasErrors ? 'caution' : 'neutral';
  }

  splitCamelCaseWithAbbreviations(data: string) {
    return data.split(/([A-Z][a-z]+)/).filter((value, index) => {
      return index === 0
        ? String(value).charAt(0).toUpperCase() + String(value).slice(1)
        : value.toLowerCase();
    });
  }

  getFormErrors(formGoup: FormGroup): string {
    let errors = '';
    Object.entries(formGoup.controls).forEach(([name, ctrl]) => {
      this.sharedService.logger('Employee DL and info, errors in form', [
        name,
        ctrl.status,
        ctrl.errors,
      ]);
      if (ctrl.status === 'INVALID') {
        if (errors) {
          errors = `${this.splitCamelCaseWithAbbreviations(name).join(' ').toLowerCase()} is invalid, ${errors}`;
        } else {
          errors = `${this.splitCamelCaseWithAbbreviations(name).join(' ').toLowerCase()} is invalid`;
        }
      }
    });
    return errors;
  }

  checkForExistingErrorsInForm() {}

  async onFileUpload(formGroup: FormGroup, event: Event) {
    try {
      this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
      const input = (event.target as HTMLInputElement) || null;

      formGroup.get('base64ConsentFile')?.patchValue('');

      const file: File | null = input?.files?.length ? input.files[0] : null;

      if (!file) {
        return;
      }

      const fileSize = file.size;

      if (fileSize < parseInt(this.minimumFileSize) * 1000) {
        input.value = '';
        formGroup.get('base64ConsentFile')?.markAsPristine();
        formGroup.get('consentFile')?.markAsPristine();
        throw Error(
          'Please provide a valid PDF consent document of atleast ' +
            this.minimumFileSize +
            ' KB.'
        );
      } else if (fileSize > parseInt(this.maximumFileSize) * 1000) {
        input.value = '';
        formGroup.get('base64ConsentFile')?.markAsPristine();
        formGroup.get('consentFile')?.markAsPristine();
        throw Error(
          'Please provide a valid PDF consent document under ' +
            this.maximumFileSize +
            ' KB.'
        );
      } else if (file.type !== 'application/pdf') {
        input.value = '';
        formGroup.get('base64ConsentFile')?.markAsPristine();
        formGroup.get('consentFile')?.markAsPristine();
        throw Error('Please upload a valid consent PDF document.');
      }

      const base64File = await this.convertFileToBase64(file);
      // formGroup.get('consentFile')?.patchValue(base64File);
      formGroup.get('base64ConsentFile')?.patchValue(base64File);
      // formGroup.get('base64ConsentFile')?.markAsPristine();
      formGroup.get('base64ConsentFile')?.markAsDirty();
      formGroup.get('consentFile')?.markAsDirty();
    } catch (error) {
      this.handleError(error);
    } finally {
      this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
    }
  }

  showDeleteButtonInAuthorizedDriverSection(): boolean {
    // always show
    return true;
  }

  showEmailConsentButtonInAuthorizedDriverSection(
    formGroup: FormGroup
  ): boolean {
    // if employee form is dirty don't show
    if (this.employeeForm?.dirty) {
      return false;
    }

    // if employee form is invalid don't show
    if (!this.employeeForm?.valid) {
      return false;
    }

    if (formGroup?.invalid) {
      // if form is invalid
      return false;
    }

    if (
      formGroup?.dirty &&
      formGroup.get('base64ConsentFile')?.getRawValue() == ''
    ) {
      // if form is dirty and no consent file uploaded
      return true;
    }

    if (formGroup.get('base64ConsentFile')?.getRawValue() !== '') {
      // if form has consent file
      return false;
    }

    if (formGroup.get('consentInd')?.getRawValue()) {
      // if already consented
      return false;
    }

    if (formGroup.get('userActionIndicator')?.getRawValue() === 'R') {
      // if form is for reactivated driver
      return true;
    }

    return false;
  }

  showSubmitButtonInAuthorizedDriverSection(formGroup: FormGroup): boolean {
    if (formGroup.get('base64ConsentFile')?.getRawValue() !== '') {
      // if consent file is uploaded
      return true;
    }

    return false;
  }

  showAddNewFormButtonInAuthorizedDriverSection(): boolean {
    const areAnyAuthorizedDriverFormUpdated = this.authorizedDriverForms?.some(
      (formGroup) =>
        formGroup.dirty && formGroup.get('_isExistingData')?.getRawValue() // if any existing auth driver form is updated
    );

    const areAnyAuthorizedDriverFormNew = this.authorizedDriverForms?.some(
      (formGroup) => !formGroup.get('_isExistingData')?.getRawValue() // if any new auth driver form is added
    );

    const isEmployeeFormUpdated =
      this.employeeForm.dirty &&
      this.employeeForm.get('_isExistingData')?.getRawValue(); // if employee form is updated

    const isEmployeeFormNew = !this.employeeForm
      .get('_isExistingData')
      ?.getRawValue(); // if employee form is new

    const isEmployeeFormInvalid = !this.employeeForm.valid; // if employee form is invalid

    // if existing employee and no auth driver
    if (
      this.employeeForm.get('_isExistingData')?.getRawValue() === true &&
      this.authorizedDriverForms?.length === 0
    ) {
      return true;
    }

    if (
      areAnyAuthorizedDriverFormUpdated ||
      areAnyAuthorizedDriverFormNew ||
      isEmployeeFormUpdated ||
      isEmployeeFormNew ||
      isEmployeeFormInvalid
    ) {
      return false;
    }
    return true;
  }

  showModal(
    type:
      | 'emailConsentToAuthorizedDriver'
      | 'submitEmployeeForm'
      | 'submitAuthorizedDriverForm'
      | 'deleteAuthorizedDriver',
    formGroup: FormGroup
  ) {
    this.handleResetModal();
    switch (type) {
      case 'emailConsentToAuthorizedDriver':
        this.modalData.showModal = true;
        this.modalData.type = type;
        this.modalData.title = 'Email Consent to Authorized Driver';
        this.modalData.body = [
          `Data is updated and consent email sent successfully to ${this.userProfile?.email || this.employeeForm.getRawValue()?.email || ''}${formGroup.get('email')?.value ? ` and ${formGroup.get('email')?.value}` : ``}. Please check both your regular email inbox and your spam folder.`,
        ];
        this.modalData.link = formGroup.getRawValue()?.base64ConsentFile;
        this.modalData.formGroup = formGroup;
        formGroup.get('base64ConsentFile')?.patchValue('');
        break;
      case 'submitEmployeeForm':
        this.modalData.showModal = true;
        this.modalData.type = type;
        this.modalData.title = 'Consent';
        this.modalData.body = [
          ` In connection with my employment at ${this.userProfile?.hrCompany} (the "Company"), I
          consent to the release of my Motor Vehicle Records (MVR) to the
          company because I have requested a Company vehicle for my use. I
          understand the company will use these records in connection with
          matters of motor vehicle or driver safety. I understand the following:`,

          `This consent is given in satisfaction of Public Law 18 U.S.C. 2721 et.
          seq., "Federal Drivers Privacy Protection Act," and applicable state
          law, and is intended to constitute "written consent" as required by
          state and federal law. I certify that the information provided is true
          and complete to the best of my knowledge. I understand that any
          falsification, misrepresentation, omission, or concealment of
          information may lead to disqualification from participation in the
          Company's vehicle program(s) and disciplinary action, up to and
          including, immediate termination of employment and/or group vehicle
          privileges.`,
        ];
        this.modalData.checkboxes = [
          {
            id: 1,
            clause: `I consent to the periodic review and evaluation of any MVR I obtained by or provided to the Company during the time I am authorized to operate a Company vehicle.`,
            isChecked: false,
          },

          {
            id: 2,
            clause: `I have read and agree to comply with the requirements of the BMW Group Employee Vehicle Policy.`,
            isChecked: false,
          },

          {
            id: 3,
            clause: `I will be solely responsible for any tolls and fines for parking or moving violations, towing storage or impoundment incurred by the Company vehicle I am authorized to operate.`,
            isChecked: false,
          },

          {
            id: 4,
            clause: `I must report any motor vehicle violation or alcohol or drug related driving offense involving my use of the Company vehicle within 48 hours of the incident.`,
            isChecked: false,
          },

          {
            id: 5,
            clause: `Where permitted, this authorization shall remain in effect over the course of my employment and MVR reports may be ordered periodically during the course of my employment during the time that I am in possession of a Company vehicle.`,
            isChecked: false,
          },

          {
            id: 6,
            clause: `In connection with my request for a family member to be an authorized driver of a BMW Group USA (the "Company") vehicle, I consent to and understand the following:`,
            isChecked: false,
            innerClauses: [
              `I agree to provide a copy of the BMW Group Employee Vehicle Policy to the family member for whom I am requesting a Company Vehicle.`,
              `I understand that the family member is required to maintain a valid driver's licenses while authorized to operate a Company vehicle.`,
              `I acknowledge that I will be solely responsible for any tolls and fines for parking or moving violations, towing storage or impoundment incurred on the Company vehicle for which the family has been authorized to operate.`,
              `I agree it is my obligation to immediately report, in writing, within 48 hours of DMV notification, to Vehicle Administration or Human Resources: (i) any driver's license suspension or expiration of the family member authorized to drive a Company vehicle; and/or (ii) any damage or accident involving damage to a Company vehicle or any alcohol or drug-related driving offense involving the family member's use of the Company vehicle. I agree to advise the family member that I am obligated to report to the Company within 48 hours any information covered under (i) or (ii) herein.`,
              `I attest that I and my family member meet all requirements for participation in the BMW Group Vehicle Program.`,
            ],
          },
        ];
        this.modalData.formGroup = formGroup;
        break;
      case 'submitAuthorizedDriverForm':
        this.modalData.showModal = true;
        this.modalData.type = type;
        this.modalData.title = 'Consent';
        this.modalData.body = [
          ` In connection with my employment at ${this.userProfile?.hrCompany} (the "Company"), I
          consent to the release of my Motor Vehicle Records (MVR) to the
          company because I have requested a Company vehicle for my use. I
          understand the company will use these records in connection with
          matters of motor vehicle or driver safety. I understand the following:`,

          `This consent is given in satisfaction of Public Law 18 U.S.C. 2721 et.
          seq., "Federal Drivers Privacy Protection Act," and applicable state
          law, and is intended to constitute "written consent" as required by
          state and federal law. I certify that the information provided is true
          and complete to the best of my knowledge. I understand that any
          falsification, misrepresentation, omission, or concealment of
          information may lead to disqualification from participation in the
          Company's vehicle program(s) and disciplinary action, up to and
          including, immediate termination of employment and/or group vehicle
          privileges.`,
        ];
        this.modalData.checkboxes = [
          {
            id: 1,
            clause: `I consent to the periodic review and evaluation of any MVR I obtained by or provided to the Company during the time I am authorized to operate a Company vehicle.`,
            isChecked: false,
          },

          {
            id: 2,
            clause: `I have read and agree to comply with the requirements of the BMW Group Employee Vehicle Policy.`,
            isChecked: false,
          },

          {
            id: 3,
            clause: `I will be solely responsible for any tolls and fines for parking or moving violations, towing storage or impoundment incurred by the Company vehicle I am authorized to operate.`,
            isChecked: false,
          },

          {
            id: 4,
            clause: `I must report any motor vehicle violation or alcohol or drug related driving offense involving my use of the Company vehicle within 48 hours of the incident.`,
            isChecked: false,
          },

          {
            id: 5,
            clause: `Where permitted, this authorization shall remain in effect over the course of my employment and MVR reports may be ordered periodically during the course of my employment during the time that I am in possession of a Company vehicle.`,
            isChecked: false,
          },

          {
            id: 6,
            clause: `In connection with my request for a family member to be an authorized driver of a BMW Group USA (the "Company") vehicle, I consent to and understand the following:`,
            isChecked: false,
            innerClauses: [
              `I agree to provide a copy of the BMW Group Employee Vehicle Policy to the family member for whom I am requesting a Company Vehicle.`,
              `I understand that the family member is required to maintain a valid driver's licenses while authorized to operate a Company vehicle.`,
              `I acknowledge that I will be solely responsible for any tolls and fines for parking or moving violations, towing storage or impoundment incurred on the Company vehicle for which the family has been authorized to operate.`,
              `I agree it is my obligation to immediately report, in writing, within 48 hours of DMV notification, to Vehicle Administration or Human Resources: (i) any driver's license suspension or expiration of the family member authorized to drive a Company vehicle; and/or (ii) any damage or accident involving damage to a Company vehicle or any alcohol or drug-related driving offense involving the family member's use of the Company vehicle. I agree to advise the family member that I am obligated to report to the Company within 48 hours any information covered under (i) or (ii) herein.`,
              `I attest that I and my family member meet all requirements for participation in the BMW Group Vehicle Program.`,
            ],
          },
        ];
        this.modalData.formGroup = formGroup;

        break;
      case 'deleteAuthorizedDriver':
        this.modalData.showModal = true;
        this.modalData.type = type;
        this.modalData.title = 'Delete Authorized Driver';
        this.modalData.body = [
          `Are you sure you want to delete authorized driver ${formGroup.get('firstName')?.value} ${formGroup.get('middleName')?.value} ${formGroup.get('lastName')?.value}?`,
        ];
        this.modalData.formGroup = formGroup;

        break;
      default:
        break;
    }
  }

  handleModalConfirmation() {
    this.modalData.confirmed = true;
    this.handleResetModal();
  }

  areAllModalCheckboxChecked(): boolean {
    const areAllChecked = this.modalData.checkboxes?.every(
      (clause) => clause.isChecked
    )
      ? true
      : false;
    return areAllChecked;
  }

  handleResetModal() {
    this.modalData.showModal = false;
    this.modalData.confirmed = false;
    this.modalData.type = '';
    this.modalData.title = '';
    this.modalData.body = [``];
    this.modalData.link = '';
    this.modalData.checkboxes = [];
  }

  handleShowInlineMessageForForcedUpdateMVR() {
    this.inlineMessage.tone = 'critical';
    // this.inlineMessage.message[0].messageTexts.push('11');
    // this.inlineMessage.message[0].messageTexts.push('22');

    const baseError = 'Unable to order MVR. Please rectify data for';
    const buildError = (data: any) => {
      const name = [data.firstName, data.middleName, data.lastName, data.suffix]
        .filter((value) => !!value?.trim())
        .join(' ');
      const detail = ' please update driver license details.';

      this.inlineMessage.message[1].messageTexts.push(
        `${baseError}, for user ${name}, ${detail}`
      );
    };

    const employeeForm = this.employeeForm;

    if (
      (employeeForm.get('orderMvrStatus')?.getRawValue() === 'failed' ||
        employeeForm.get('orderMvrStatus')?.getRawValue() === 'pending') &&
      employeeForm.get('consentInd')?.getRawValue() === true
    ) {
      buildError(employeeForm.getRawValue());
    }

    this.authorizedDriverForms.forEach((authorizedDriverForm) => {
      if (
        (authorizedDriverForm.get('orderMvrStatus')?.getRawValue() ===
          'failed' ||
          authorizedDriverForm.get('orderMvrStatus')?.getRawValue() ===
            'pending') &&
        authorizedDriverForm.get('consentInd')?.getRawValue() === true
      ) {
        buildError(authorizedDriverForm.getRawValue());
      }
    });

    if (this.inlineMessage.message[0].messageTexts.length !== 0) {
      this.inlineMessage.showInlineMessage = true;
    }
  }

  handleShowInlineMessageForForceUpdateDriverLicense() {
    this.inlineMessage.tone = 'critical';

    const baseError =
      'Please update driver license information as it appears in the license';
    const buildError = (data: any) => {
      const name = [data.firstName, data.middleName, data.lastName, data.suffix]
        .filter((value) => !!value?.trim())
        .join(' ');
      const detail =
        data.forcedDLMessage?.trim() || ' please update driver license details';

      this.inlineMessage.message[1].messageTexts.push(
        `${baseError}, for user ${name}, ${detail}`
      );
    };

    const employeeForm = this.employeeForm;

    if (
      employeeForm.get('forcedDLUpdateInd')?.getRawValue() === true &&
      employeeForm.get('consentInd')?.getRawValue() === true
    ) {
      buildError(employeeForm.getRawValue());
    }

    this.authorizedDriverForms.forEach((authorizedDriverForm) => {
      if (
        authorizedDriverForm.get('forcedDLUpdateInd')?.getRawValue() === true &&
        authorizedDriverForm.get('consentInd')?.getRawValue() === true
      ) {
        buildError(authorizedDriverForm.getRawValue());
      }
    });

    if (this.inlineMessage.message[1].messageTexts.length !== 0) {
      this.inlineMessage.showInlineMessage = true;
    }
  }

  handleShowInlineMessageForExistingInvalidFormData() {
    this.inlineMessage.tone = 'critical';

    const employeeFormError = `${this.checkInvalidDataInForm(this.employeeForm)}`;

    const authorizedDriverFormError = [];

    authorizedDriverFormError.push(
      this.authorizedDriverForms.map((form) => {
        return this.checkInvalidDataInForm(form);
      })
    );

    // if (employeeFormError) {
    //   this.inlineMessage.showInlineMessage = true;
    //   this.inlineMessage.message[2].messageTexts.push(`${employeeFormError}`);
    // }

    // if (authorizedDriverFormError) {
    //   this.inlineMessage.showInlineMessage = true;
    //   this.inlineMessage.message[2].messageTexts.push(`jj`);
    // }
    this.sharedService.logger('Employee DL and info - existing data error', [
      authorizedDriverFormError,
      employeeFormError,
    ]);
  }

  checkAllmodalCheckbox() {
    this.modalData.checkboxes?.forEach((data) => {
      data.isChecked = true;
    });
  }

  sanitizeForm(
    form:
      | EmployeeFormGroup
      | AuthorizedDriverFormGroup
      | DeactivatedAuthorizedDriverFormGroup
  ):
    | AuthorizedDriverFormData
    | EmployeeFormData
    | DeactivatedAuthorizedDriverFormData {
    const formData = form.getRawValue();
    const sanitizedFormData:
      | AuthorizedDriverFormData
      | EmployeeFormData
      | DeactivatedAuthorizedDriverFormData = {
      employeeId: formData.employeeId?.trim(),
      driverLicenseNumber: formData.driverLicenseNumber?.trim(),
      countryCode: formData.countryCode?.trim(),
      stateProvinceCode: formData.stateProvinceCode?.trim(),
      firstName: formData.firstName?.trim(),
      middleName: formData.middleName?.trim(),
      lastName: formData.lastName?.trim(),
      suffix: formData.suffix?.trim(),
      last4DigitsSSN:
        this.isSSNRequired(
          formData.countryCode?.trim(),
          formData.stateProvinceCode?.trim()
        ) === false
          ? ''
          : formData.last4DigitsSSN?.trim(),
      zipCode: formData.zipCode?.trim(),
      driverLicenseClass: formData.driverLicenseClass?.trim().toUpperCase(),
      addressLine1: formData.addressLine1?.trim(),
      addressLine2: formData.addressLine2?.trim(),
      city: formData.city?.trim(),
      createdByUser: formData.createdByUser?.trim(),
      modUser: this.userProfile?.qxid || formData.modUser?.trim(),
      email: formData.email?.trim(),
      orderMvrStatus: formData.orderMvrStatus?.trim(),
      previousStateProvinceCode: formData.previousStateProvinceCode?.trim(),
      relationToEmployee: formData.relationToEmployee?.trim(),
      customPersonId: formData.customPersonId?.trim(),
      hrCompany: formData.hrCompany?.trim(),
      previousDriverLicenseNumber: formData.previousDriverLicenseNumber?.trim(),
      userActionIndicator: formData.userActionIndicator?.trim() as
        | 'U'
        | 'N'
        | 'A'
        | 'R',
      modTimeStampUtc: formData.modTimeStampUtc?.trim() || '',
      createdDate: formData.createdDate?.trim() || '',
      expiryDate: this.convertDateToMMDDYYYY(
        formData.expiryDate?.toDateString().trim() || ''
      ),
      dateOfBirth: this.convertDateToMMDDYYYY(
        formData.dateOfBirth?.toDateString().trim() || ''
      ),
      consentUploadedTimestamp: formData.consentUploadedTimestamp?.trim() || '',
      lastConsentedTimeStamp:
        this.formatLastConsentedTimestamp(
          formData.lastConsentedTimeStamp.trim()
        ) || '',
      forcedDLMessage: formData.forcedDLMessage.trim(),
      authorisedUserInd: formData.authorisedUserInd === true ? 'Y' : 'N',
      mcEndorsementInd: formData.mcEndorsementInd === true ? 'Y' : 'N',
      activeInd: formData.activeInd === true ? 'Y' : 'N',
      primaryDriverInd: formData.primaryDriverInd === true ? 'Y' : 'N',
      consentInd: formData.consentInd === true ? 'Y' : 'N',
      forcedDLUpdateInd: formData.forcedDLUpdateInd === true ? 'Y' : 'N',
      deactivateInd: formData.deactivateInd === true ? 'Y' : 'N',
      previousConsentInd: formData.previousConsentInd === true ? 'Y' : 'N',
      consentFile: formData.consentFile,
      base64ConsentFile: formData.base64ConsentFile,
    };

    if ('under18Indicator' in formData) {
      sanitizedFormData.under18Indicator =
        formData.under18Indicator === true ? 'Y' : 'N';
    }

    if (
      form.dirty &&
      formData._isExistingData &&
      sanitizedFormData.userActionIndicator === 'R'
    ) {
      sanitizedFormData.userActionIndicator = 'R';
      sanitizedFormData.consentInd = 'N';
    } else if (form.dirty && formData._isExistingData) {
      sanitizedFormData.userActionIndicator = 'U';
      sanitizedFormData.consentInd = 'N';
    } else if (form.dirty && !formData._isExistingData) {
      sanitizedFormData.userActionIndicator = 'A';
      sanitizedFormData.previousConsentInd = '';
    }

    return sanitizedFormData;
  }

  downloadConsent(
    html: string,
    modalData: EmployeeDriverLicenseAndInformationModalData
  ) {
    const blob = new Blob([html]);
    URL.createObjectURL(blob);
    const newWindow: any = window.open('', '_blank');

    if (!newWindow) {
      throw Error('Unable to generate consent PDF document.');
      return; // popup blocked?
    }
    // newWindow.document.body.innerHTML = html;
    newWindow.document.write(html);
    // newWindow.document.title = `_consent_document.pdf`;

    newWindow.document.title = `${this.employeeForm?.get('lastName')?.value || ''}_${this.employeeForm?.get('firstName')?.value || ''}_${modalData.formGroup?.get('lastName')?.value || ''}_${modalData.formGroup?.get('firstName')?.value || ''}_consent_document.pdf`;
    newWindow.document.close();
    newWindow.onafterprint = () => {
      newWindow.close();
    };
    newWindow.onload = () => {
      setTimeout(() => {
        newWindow.focus();
        newWindow.print();
      }, 500);
    };
  }
}
